import { QueryErrorResetBoundary, QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query"
import { AgTechHttpRequester, useAgTechHttpRequester } from "../http/AgTechHttp"
import { MutableRefObject, useEffect, useRef } from "react";
import { logDev, logDevError } from "../../logging/AgTechLogger";
import { useAppContext } from "../../app/AgTechAppContext";
import { AgTechFeatureContextType, useAgTechFeatureContext } from "../../features/AgTechFeatures";

///
/// Query Types
///

type AgTechQueryDefinitions<TQueryInputData, TQueryResponseData> = 
    AgTechQuery<TQueryInputData, TQueryResponseData> |
    AgTechQueryProjection<TQueryInputData, any, TQueryResponseData>;

export type AgTechQuery<TQueryInputData, TQueryResponseData> = {
    queryKey: (contextData: TQueryInputData) => QueryKey,
    query: (props: AgTechQueryFunctionProps<TQueryInputData>) => Promise<AgTechQueryResponse<TQueryResponseData>>,
    validation?: AgTechQueryValidationConfiguration<TQueryResponseData>,
    content: AgTechQueryContentConfiguration,
    settings?: (props: AgTechQueryFunctionProps<TQueryInputData>) => Omit<UseQueryOptions<TQueryResponseData, unknown, TQueryResponseData>, 'queryKey'>
}

export type AgTechQueryProjection<TQueryInputData, TQueryResponseData, TQueryProjectionData> = {
    query: AgTechQuery<TQueryInputData, TQueryResponseData>,
    projection: (props: AgTechQueryFunctionProps<TQueryInputData>, data: TQueryResponseData) => TQueryProjectionData,
    validation?: AgTechQueryValidationConfiguration<TQueryProjectionData>,
    content?: AgTechQueryContentConfiguration
}

export type AgTechQueryCombination<TQueryInputData, TQueryResponseData, TOtherQueryResponseData, TQueryProjectionData> = {
    first: (props: AgTechQueryInput<TQueryInputData>) => AgTechQueryProp<TQueryInputData, TQueryResponseData>,
    second: (props: AgTechQueryInput<TQueryInputData>) => AgTechQueryProp<any, TOtherQueryResponseData>,
    projection: (props: AgTechQueryFunctionProps<TQueryInputData>, data: TQueryResponseData, otherData: TOtherQueryResponseData) => TQueryProjectionData,
    validation?: AgTechQueryValidationConfiguration<TQueryProjectionData>,
    content?: AgTechQueryContentConfiguration
}

export const AgTechDefaultQuery: AgTechQuery<any, any> = {
    queryKey: () => ['Default'],
    query: async () => AgTechQueryResponses.Success({}),
    content: {
        loadingText: 'Loading...',
        failureText: 'Failed to load.'
    }
}

///
/// Query Actions
///

export type AgTechQueryWithActions<TQueryInputData, TQueryResponseData, TQueryActions extends {}> = AgTechQuery<TQueryInputData, TQueryResponseData> & {
    actions: TQueryActions
}

export type AgTechQueryAction<TQueryResponseData> = <TActionInput, >(existingData: TQueryResponseData, input: TActionInput) => TQueryResponseData

///
/// Query Execution Props
///

export type AgTechQueryProp<TQueryInputData, TQueryResponseData> = {
    query: AgTechQueryDefinitions<TQueryInputData, TQueryResponseData>
} & AgTechQueryInput<TQueryInputData>;

export type AgTechQueryInput<TQueryInputData> = 0 extends (1 & TQueryInputData)
    ? Partial<{ queryContext: any }>
    : { queryContext: TQueryInputData };

export type AgTechQueryFunctionProps<TQueryInputData> = {
    inputData: TQueryInputData,
    http: AgTechHttpRequester,
    features: AgTechFeatureContextType
}

///
/// Query Settings
///

export type AgTechQuerySettings<TQueryResponseData> = {
    content?: AgTechQueryContentConfiguration,
    validation?: AgTechQueryValidationConfiguration<TQueryResponseData>,
    eventHandlers?: AgTechQueryEventHandlers<TQueryResponseData>
}
& Omit<UseQueryOptions<TQueryResponseData, unknown, TQueryResponseData>, 'queryKey'>;

export type AgTechQueryValidationConfiguration<TProjectedQueryResponseData> = {
    validate: (data: TProjectedQueryResponseData) => string | undefined,
    retryButtonTextOnFailure?: string,
    retryButtonIconOnFailure?: string
}

export type AgTechQueryContentConfiguration = {
    loadingText: string,
    failureText: string,
}

export type AgTechQueryEventHandlers<TQueryResponseData> = {
    onQuerySuccess?: (data: TQueryResponseData) => void,
    onQueryFailed?: () => void
}

///
/// Query Results
///

export type AgTechQueryResult<TQueryResponseData> = {
    isLoading: boolean,
    isRefreshing: boolean,
    hasSucceeded: boolean,
    hasFailed: boolean,
    data: TQueryResponseData,
    error?: any,
    refresh: () => Promise<any>,
    query: any
}

export type AgTechQueryRun<TQueryResponseData> ={
    queryKey: QueryKey,
    queryResult: AgTechQueryResult<TQueryResponseData>,
    validation?: AgTechQueryValidationConfiguration<TQueryResponseData>,
    content: AgTechQueryContentConfiguration,
    refresh: () => Promise<any>
}

export type AgTechQueryResponse<TResponseData> = {
    errorMessage?: string,
    success?: {
        data: TResponseData
    }
}

export class AgTechQueryResponses {
    static Success<TResponseData>(data: TResponseData): AgTechQueryResponse<TResponseData> {
        return {
            success: {
                data: data
            }
        }
    }

    static Failure<TResponseData>(errorMessage: string): AgTechQueryResponse<TResponseData> {
        return {
            errorMessage: errorMessage
        }
    }
}


///
/// Query Builders
///

export type AgTechQueryBuilderType = {
    buildDirectQuery: <TQueryInput, TQueryData>(props: {
        query: AgTechQuery<TQueryInput, TQueryData>,
        data: TQueryData
    }) => AgTechQuery<TQueryInput, TQueryData>,
    buildOptionalQuery: <TQueryInput, TQueryData>(props: {
        query: AgTechQuery<TQueryInput, TQueryData>,
        data?: TQueryData,
        dataCheck?: (data?: TQueryData) => boolean
    }) => AgTechQuery<TQueryInput, TQueryData>,
    buildQueryResult: <TQueryData, >(props: {
        data: TQueryData
    }) => AgTechQueryRun<TQueryData>
}

export const AgTechQueryBuilder: AgTechQueryBuilderType = {
    // Direct queries mock the query function and return the provided data
    // synchronously. This is useful when data is already available but
    // needs to be passed through the query system.
    buildDirectQuery: <TQueryInput, TQueryData>(props: {
        query: AgTechQuery<TQueryInput, TQueryData>,
        data: TQueryData
    }): AgTechQuery<TQueryInput, TQueryData> => {
        return {
            queryKey: props.query.queryKey,
            query: async () => AgTechQueryResponses.Success(props.data),
            validation: props.query.validation,
            content: props.query.content,
            settings: props.query.settings
        }
    },
    // Optional queries allow for literal values to be passed alongside true queries.
    // The true queries will be executed only if the literal value is null or undefined.
    buildOptionalQuery: <TQueryInput, TQueryData>(
        props: {
            query: AgTechQuery<TQueryInput, TQueryData>,
            data?: TQueryData,
            dataCheck?: (data?: TQueryData) => boolean
        }
    ): AgTechQuery<TQueryInput, TQueryData> => {
        const dataCheck = props.dataCheck ?? ((data: TQueryData | undefined) => data !== null && data !== undefined);

        return dataCheck(props.data)
            ? AgTechQueryBuilder.buildDirectQuery({
                query: props.query,
                data: props.data!
            })
            : props.query;
    },
    buildQueryResult: <TQueryData, >(props: {
        data: TQueryData
    }): AgTechQueryRun<TQueryData> => {
        return {
            queryKey: [],
            queryResult: {
                data: props.data,
                hasFailed: false,
                isLoading: false,
                hasSucceeded: true,
            } as any,
            content: {
                loadingText: '',
                failureText: ''
            },
            refresh: async () => {}
        }
    }
}

export const queryResult = <TData, >(data: TData): AgTechQueryRun<TData> => {
    return AgTechQueryBuilder.buildQueryResult({ data: data });
}

///
/// Hooks
///

export function useAgTechQuery<TQueryInputData, TQueryResponseData, TQueryProjectionData = TQueryResponseData>(
    props: AgTechQueryProp<TQueryInputData, TQueryResponseData> &
    {
        settings?: AgTechQuerySettings<TQueryResponseData>,
        projection?: (data: TQueryResponseData) => TQueryProjectionData,
        loggingContext?: object,
        caller?: string | undefined
    }
): AgTechQueryRun<TQueryProjectionData> {
    const appContext = useAppContext();
    const featureContext = useAgTechFeatureContext();

    let caller = props.caller;

    if (!caller) {
        const stack = new Error().stack;
        const callerLine = stack?.split('\n')[2]?.trim();

        caller = callerLine?.split(' ')[1];
    }

    const queryExecutionLog = useRef<any>({
        query: {
            context: props.queryContext,
        },
        ...props.loggingContext,
        caller: caller,
        hasBeenLogged: false
    });

    const query = getQuery(props.query, queryExecutionLog);
    const queryContext = props.queryContext as any;
    const queryKey = query.queryKey(queryContext);

    queryExecutionLog.current.query.key = queryKey;

    const httpExecutor = useAgTechHttpRequester(queryExecutionLog);

    const queryProps = {
        inputData: queryContext,
        http: httpExecutor,
        features: featureContext
    };

    const queryRun = useQuery({
        retry: false,
        staleTime: props?.settings?.staleTime ?? 1000 * 600,
        refetchOnWindowFocus: false,
        ...query.settings?.(queryProps),
        ...props.settings,
        queryKey: queryKey,
        queryFn: async () => {           
            const response = await query.query(queryProps);

            if (response?.success) {
                return response.success.data
            }
            else {
                throw response?.errorMessage ?? 'An error occurred';
            }
        }
    });

    useEffect(() => {
        if (queryRun.isSuccess) {
            props.settings?.eventHandlers?.onQuerySuccess?.(queryRun.data! as any);

            if (!queryExecutionLog.current.hasBeenLogged) {
                queryExecutionLog.current.hasBeenLogged = true;

                logDev('[Query Executed]', queryKey, {
                    ...queryExecutionLog.current,
                    run: queryRun,
                    query: {
                        ...queryExecutionLog.current.query,
                        queryKey: queryKey,
                        inputData: queryContext,
                        hasProjection: props.projection !== undefined,
                        queryData: queryRun.data,
                        responseData: queryResult.data
                    },
                    response: queryResult.data
                });
            }
        }
        else if (queryRun.isError) {
            props.settings?.eventHandlers?.onQueryFailed?.();

            logDevError('[Query Failed - ' + queryKey + ']', {
                ...queryExecutionLog.current,
                query: {
                    ...queryExecutionLog.current.query,
                    queryKey: queryKey,
                    inputData: queryContext,
                    hasProjection: props.projection !== undefined,
                    queryData: queryRun.data,
                    error: queryRun.error
                }
            });

            appContext.messaging.showError({
                header: 'Query Failed - ' + queryKey,
                details: JSON.stringify(queryExecutionLog.current, null, 4)
            });
        }
    }, [queryRun.isSuccess, queryRun.isError, queryRun.data]);

    const isQueryLoading = queryRun.isLoading;

    const queryResult: AgTechQueryResult<TQueryProjectionData> = {
        isLoading: isQueryLoading,
        isRefreshing: queryRun.isFetching || queryRun.isRefetching,
        hasSucceeded: !isQueryLoading && queryRun?.isSuccess && queryRun?.data !== undefined,
        error: queryRun.error,
        hasFailed: queryRun?.isError || (queryRun?.data !== undefined && queryRun?.data === undefined),
        data: props.projection && queryRun?.data
            ? props.projection(queryRun?.data as any)
            : queryRun?.data as TQueryProjectionData,
        refresh: queryRun.refetch,
        query: queryRun
    }

    return {
        queryKey: queryKey,
        queryResult: queryResult,
        content: query.content,
        refresh: queryRun.refetch
    }
}

export const useAgTechOptionalQuery = <TQueryInputData, TQueryData, TProjectionData = TQueryData>(
    props: { query: AgTechQueryDefinitions<TQueryInputData, TQueryData> } & AgTechQueryInput<TQueryInputData> & {
        data?: TQueryData,
        dataCheck?: (data?: TQueryData) => boolean,
        projection?: (data: TQueryData) => TProjectionData
    }
): AgTechQueryRun<TProjectionData> => {
    const dataCheck = props.dataCheck ?? ((data: TQueryData | undefined) => data !== null && data !== undefined);
    const hasDataBeenProvided = dataCheck(props.data);

    const optionalQueryLoggingContext = {
        options: {
            providedData: props.data,
            wasDataProvided: hasDataBeenProvided
        }
    }

    const stack = new Error().stack;
    const callerLine = stack?.split('\n')[2]?.trim(); // Adjust index based on the environment
    const caller = callerLine?.split(' ')[1];

    const query = useAgTechQuery({
        query: hasDataBeenProvided
            ? AgTechDefaultQuery
            : props.query,
        queryContext: props.queryContext,
        projection: props.projection,
        loggingContext: optionalQueryLoggingContext,
        caller: caller
    });

    return hasDataBeenProvided
        ? AgTechQueryBuilder.buildQueryResult({
            data: props.projection
                ? props.projection(props.data!)
                : props.data! as TProjectionData
        })
        : query;
}

export const useAgTechQueryCombination = <
    TFirstQueryInputData,
    TFirstQueryResponseData,
    TSecondQueryInputData,
    TSecondQueryResponseData,
    TQueryProjectionData
>(props: {
    firstQuery: AgTechQueryProp<TFirstQueryInputData, TFirstQueryResponseData>,
    secondQuery: AgTechQueryProp<TSecondQueryInputData, TSecondQueryResponseData>,
    projection: (firstQueryData: TFirstQueryResponseData, secondQueryData: TSecondQueryResponseData) => TQueryProjectionData,
    content?: AgTechQueryContentConfiguration
})
: AgTechQueryRun<TQueryProjectionData> => {
    const stack = new Error().stack;
    const callerLine = stack?.split('\n')[2]?.trim(); // Adjust index based on the environment
    const caller = callerLine?.split(' ')[1];

    const firstQuery = useAgTechQuery({
        query: props.firstQuery.query,
        queryContext: props.firstQuery.queryContext,
        caller: caller
    });

    const secondQuery = useAgTechQuery({
        query: props.secondQuery.query,
        queryContext: props.secondQuery.queryContext,
        caller: caller
    });

    return useAgTechQueryResultCombination({
        firstQuery: firstQuery,
        secondQuery: secondQuery,
        projection: props.projection,
        content: props.content
    });
}

export const useAgTechQueryResultCombination = <
    TFirstQueryResponseData,
    TSecondQueryResponseData,
    TQueryProjectionData
>(props: {
    firstQuery: AgTechQueryRun<TFirstQueryResponseData>,
    secondQuery: AgTechQueryRun<TSecondQueryResponseData>,
    projection: (
        firstQueryData: TFirstQueryResponseData,
        secondQueryData: TSecondQueryResponseData
    ) => TQueryProjectionData,
    content?: AgTechQueryContentConfiguration
})
: AgTechQueryRun<TQueryProjectionData> => {
    const firstQuery = props.firstQuery;
    const secondQuery = props.secondQuery;

    const targetQuery = secondQuery.queryResult.isLoading || secondQuery.queryResult.isRefreshing
        ? secondQuery
        : firstQuery;

    const haveAnyQueriesFailed = firstQuery.queryResult.hasFailed || secondQuery.queryResult.hasFailed;
    const haveAllQueriesSucceeded = firstQuery.queryResult.hasSucceeded && secondQuery.queryResult.hasSucceeded;

    const queryCombo = {
        ...targetQuery,
        queryResult: {
            ...targetQuery.queryResult,
            error: firstQuery.queryResult.error ?? secondQuery.queryResult.error,
            hasFailed: haveAnyQueriesFailed,
            isError: haveAnyQueriesFailed,
            isSuccess: haveAllQueriesSucceeded,
            data: haveAllQueriesSucceeded
                ? props.projection(
                    firstQuery.queryResult.data,
                    secondQuery.queryResult.data
                )
                : undefined
        },
        refresh: async () => {
            await firstQuery.refresh();
            await secondQuery.refresh();
        },
        content: props.content ?? targetQuery.content
    } as any;

    return queryCombo;
}

export const useAgTechQueryResultProjection = <
    TQueryResponseData,
    TQueryProjectionData
>(
    props: {
        queryRun: AgTechQueryRun<TQueryResponseData>,
        projection: (queryData: TQueryResponseData) => TQueryProjectionData
    }
)
: AgTechQueryRun<TQueryProjectionData> => {
    const query = props.queryRun;
    const hasQuerySucceeded = query.queryResult.hasSucceeded;

    const queryProjection = {
        ...query,
        queryResult: {
            ...query.queryResult,
            error: query.queryResult.error,
            isError: query.queryResult.hasFailed,
            isSuccess: hasQuerySucceeded,
            data: hasQuerySucceeded
                ? props.projection(query.queryResult.data)
                : undefined
        },
        refresh: async () => {
            await query.refresh();
        },
        content: query.content
    } as any;

    return queryProjection;
}

export const useAgTechQueryHandler = <TQueryData, >(props: {
    query: AgTechQueryRun<TQueryData>,
    onQuerySuccess: (data: TQueryData) => Promise<void>
}) => {
    useEffect(() => {       
        if (props.query.queryResult.hasSucceeded) {
            props.onQuerySuccess(props.query.queryResult.data)
        }
    }, [props.query.queryResult.hasSucceeded]);
}

export const waitOnQuery = async <TData, >(query: AgTechQueryRun<TData>) => {
    return new Promise<TData>((res, rej) => {
        if (query.queryResult.hasSucceeded && query.queryResult.data) {
            res(query.queryResult.data);
        }
        else if (query.queryResult.hasFailed) {
            rej();
        }
    })
}

///
/// Query Type Checks
///

const isProjection = <TQueryInputData, TQueryResponseData>(query: AgTechQueryDefinitions<TQueryInputData, TQueryResponseData>) => {
    return (query as any).projection !== undefined;
}

const isCombination = <TQueryInputData, TQueryResponseData>(query: AgTechQueryDefinitions<TQueryInputData, TQueryResponseData>) => {
    return (query as any).otherQuery !== undefined;
}

const getQuery = <TQueryInputData, TQueryResponseData>(
    query: AgTechQueryDefinitions<TQueryInputData, TQueryResponseData>,
    queryLog: MutableRefObject<any>
) => {
    let fullQuery = query as AgTechQuery<TQueryInputData, TQueryResponseData>;

    if (isProjection(query)) {
        const projectionQuery = query as AgTechQueryProjection<TQueryInputData, any, TQueryResponseData>;

        fullQuery = {
            queryKey: (contextData: TQueryInputData) => projectionQuery.query.queryKey(contextData),
            query: async (props) => projectionQuery.query.query(props),
            validation: projectionQuery.validation,
            content: projectionQuery.content ?? projectionQuery.query.content,
            settings: (props) => ({
                select: (data: any) => {                    
                    const projectedData = projectionQuery.projection(props, data);

                    queryLog.current.projection = {
                        innerQueryData: data,
                        projectedData: projectedData
                    };

                    return projectedData;
                },
            })
        }
    };

    return fullQuery;
}